import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import AutoScheduleDiffs from "./AutoScheduleDiffs";
import { ScheduleComparisonHeader, ScheduleComparisonActions } from "./ScheduleComparisonChrome";
import { API_optimize_schedule_breaks } from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";

const buttonSx = {
  minHeight: 40, px: 2, borderRadius: 2, textTransform: "none", fontSize: 14, fontWeight: 600,
  "&.Mui-focusVisible": { outline: "2px solid #7c3aed", outlineOffset: 2 },
};
const secondaryButtonSx = {
  ...buttonSx, color: "#475569", borderColor: "#cbd5e1",
  "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" },
  "&.Mui-disabled": { color: "#94a3b8", borderColor: "#e2e8f0" },
  "&.MuiLoadingButton-loading": { color: "transparent" },
};

export function breakTime(value) {
  const minutes = Math.round(value * 60);
  const hour = ((Math.floor(minutes / 60) % 24) + 24) % 24;
  return `${hour % 12 || 12}:${String(((minutes % 60) + 60) % 60).padStart(2, "0")}${hour < 12 ? "a" : "p"}${value >= 24 ? " (+1 day)" : ""}`;
}

function BreakList({ items, previous }) {
  return items.length ? <ul className="space-y-1.5">
    {items.map((item, index) => {
      const changed = previous && !previous.some((old) => old.label === item.label &&
        Math.abs(old.start - item.start) < 1e-8 && Math.abs(old.duration - item.duration) < 1e-8);
      return <li key={index} className={`rounded-md px-2.5 py-2 ${changed ? "bg-violet-50" : "bg-slate-50"}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-xs text-slate-500">
          <span>{item.label}</span><span>{Math.round(item.duration * 60)} min</span>
        </div>
        <p className={`mt-1 text-sm ${changed ? "font-semibold text-violet-800" : "text-slate-600"}`}>
          {breakTime(item.start)}–{breakTime(item.start + item.duration)}
          {changed && <span className="sr-only"> (updated)</span>}
        </p>
      </li>;
    })}
  </ul> : <p className="px-2.5 py-2 text-sm text-slate-500">No breaks</p>;
}

function BreakPreviewResults({ preview, comparisonProps, weekStart }) {
  const days = [...preview.changes]
    .sort((left, right) => left.date.localeCompare(right.date) || left.shift_start - right.shift_start || left.employee.localeCompare(right.employee))
    .reduce((groups, change) => {
      if (!groups[change.date]) groups[change.date] = [];
      groups[change.date].push(change);
      return groups;
    }, {});

  const placementWarning = preview.failed_break_shifts > 0 && <p role="status" className="mb-3 text-sm text-amber-700">
    Breaks couldn’t be placed on {preview.failed_break_shifts} {preview.failed_break_shifts === 1 ? "shift" : "shifts"}.
  </p>;
  if (!preview.changes.length) return <div className="space-y-3 py-4">
    <p className="text-sm text-slate-600">No break changes to apply.</p>
    {placementWarning}
  </div>;

  return <>
    {placementWarning}
    {preview.comparison && <AutoScheduleDiffs {...comparisonProps}
        week_start_date={weekStart} sched_dif={preview.comparison.sched_dif}
        schedule_differences={preview.comparison}
        actual_schedule={preview.comparison.actual_schedule}
        actual_schedule_labor={preview.comparison.actual_schedule_labor}
        forecasted_labor={preview.comparison.forecasted_labor} />}
    {!preview.comparison && !!preview.changes.length && <details open className="mt-4">
      <summary className="mb-3 cursor-pointer text-sm font-semibold text-slate-700">Break change details</summary>
      <section aria-labelledby="break-changes-heading">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="break-changes-heading" className="text-sm font-semibold text-slate-900">Changes by day</h3>
        <p className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2 w-2 rounded-full bg-violet-500" />Updated breaks are highlighted</p>
      </div>
      <div className="space-y-5">
        {Object.entries(days).map(([date, changes]) => <section key={date} aria-label={format(parseISO(date), "EEEE, MMMM d")} className="overflow-hidden rounded-xl border border-slate-200">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h4 className="text-sm font-semibold text-slate-700">{format(parseISO(date), "EEEE, MMM d")}</h4>
            <span className="text-xs text-slate-500">{changes.length} shift{changes.length === 1 ? "" : "s"}</span>
          </div>
          <div aria-hidden="true" className="grid grid-cols-3 gap-4 px-4 pb-1 pt-3 text-xs font-medium text-slate-500 sm:hidden">
            <div>Team member / shift</div><div>Current breaks</div><div className="text-violet-700">Proposed breaks</div>
          </div>
          <ul aria-label={`Break changes for ${date}`} className="divide-y divide-slate-100">
            {changes.map((change, index) => <li key={index} className="grid grid-cols-3 gap-4 p-4 sm:grid-cols-2 sm:gap-3">
              <div className="sm:col-span-2">
                <p className="text-sm font-semibold text-slate-900">{change.employee}</p>
                <p className="mt-1 text-xs text-slate-500">{breakTime(change.shift_start)}–{breakTime(change.shift_end)}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[["added", change.added], ["moved", change.moved], ["extended", change.extended]]
                    .filter(([, count]) => count > 0)
                    .map(([label, count]) => <span key={label} className="rounded-md border border-slate-200 px-1.5 py-0.5 text-xs text-slate-600">{count} {label}</span>)}
                </div>
              </div>
              <div className="min-w-0">
                <p className="sr-only sm:not-sr-only sm:mb-2 sm:text-xs sm:font-medium sm:text-slate-500">Current breaks</p>
                <BreakList items={change.before} />
              </div>
              <div className="min-w-0">
                <p className="sr-only sm:not-sr-only sm:mb-2 sm:text-xs sm:font-medium sm:text-violet-700">Proposed breaks</p>
                <BreakList items={change.after} previous={change.before} />
              </div>
            </li>)}
          </ul>
        </section>)}
      </div>
    </section></details>}
  </>;
}

export default function OptimizeBreaksDialog({ storeId, options, mode = "optimize", onClose, onApplied, comparisonProps }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("preview");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const optionsKey = JSON.stringify(options);
  const hasChanges = Boolean(preview?.changes.length);
  const runLabel = mode === "optimize" ? "Optimize breaks" : "Add missing breaks";

  useEffect(() => {
    let active = true;
    setBusy("preview");
    setPreview(null);
    setError("");
    axios.post(API_optimize_schedule_breaks, { ...JSON.parse(optionsKey), action: "preview", mode }, {
      ...getDefaultAxiosConfig(), params: { store_id: storeId },
    }).then((response) => {
      if (active) setPreview(response.data.redux.payload);
    }).catch((failure) => {
      if (active) setError(failure.response?.data?.system?.message || "Unable to find break recommendations. Please try again.");
    }).finally(() => {
      if (active) setBusy("");
    });
    return () => { active = false; };
  }, [storeId, optionsKey, mode, attempt]);

  const apply = async () => {
    setBusy("apply");
    setError("");
    try {
      const response = await axios.post(API_optimize_schedule_breaks,
        { action: "apply", preview_token: preview.preview_token }, {
          ...getDefaultAxiosConfig(), params: { store_id: storeId },
        });
      const payload = response.data.redux.payload;
      toast.success(`Breaks updated on ${payload.updated_shifts} shift${payload.updated_shifts === 1 ? "" : "s"}.`);
      try {
        await onApplied();
      } catch {
        toast.error("Breaks were saved. Refresh the schedule to see the changes.");
      }
      onClose();
    } catch (failure) {
      setError(failure.response?.data?.system?.message || "Unable to update breaks. Please try again.");
      if (failure.response?.status === 409) setPreview(null);
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open onClose={busy === "apply" ? undefined : onClose} fullWidth maxWidth={hasChanges ? false : "sm"}
      aria-labelledby="optimize-breaks-title"
      PaperProps={{ sx: { borderRadius: 3, m: { xs: 2, sm: 3 }, width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)", ...(hasChanges ? { height: "calc(100% - 32px)" } : {}) } }}>
      <ScheduleComparisonHeader id="optimize-breaks-title" runLabel={runLabel}
        title={hasChanges ? "Compare schedules" : runLabel}
        weekStart={options.date_start} onClose={onClose} busy={busy === "apply"} />
      <DialogContent sx={{ px: { xs: 1.5, sm: 2.5 }, py: hasChanges ? 1.5 : 3 }}>
        {busy === "preview" && <div role="status" className="flex items-center justify-center gap-3 py-8 text-sm text-slate-600">
          <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
          Finding break recommendations…
        </div>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {preview && <BreakPreviewResults preview={preview} comparisonProps={comparisonProps} weekStart={options.date_start} />}
      </DialogContent>
      {hasChanges ? <ScheduleComparisonActions onCancel={onClose} onApply={apply}
        busy={busy === "apply"} /> :
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={onClose} sx={secondaryButtonSx}>{busy === "preview" ? "Cancel" : "Close"}</Button>
          {error && <Button onClick={() => setAttempt((value) => value + 1)} sx={secondaryButtonSx}>Try again</Button>}
        </DialogActions>}
    </Dialog>
  );
}
